import React, { useMemo, useState } from "react";
import { Search, Play, Pause, Download, Phone, PhoneIncoming, PhoneOutgoing, Mic, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { downloadOrShareFile } from "../../utils/pdfShare";
import { toast } from "sonner";
import CallRecordingPlayer from "../audio/CallRecordingPlayer";

/**
 * Recordings tab for the Call History page.
 * Shows every call that has a recording_url, grouped by date with
 * inline play/pause + download. Searchable by name/number/date.
 *
 * Props:
 *   calls           — full call_logs list (already fetched by the parent)
 *   loading         — boolean
 *   getRecordingProxyUrl(callId)  — returns the authed audio URL
 *   formatPhoneNumber(phone)       — display helper
 *   formatDuration(secs)           — display helper
 */
const RecordingsTab = ({ calls, loading, getRecordingProxyUrl, formatPhoneNumber, formatDuration }) => {
  const [search, setSearch] = useState("");
  const [playingId, setPlayingId] = useState(null);   // call_id of the currently-playing track
  const [downloadingId, setDownloadingId] = useState(null);

  // Filter to calls that actually have a recording, then apply search
  const recordings = useMemo(() => {
    const rec = (calls || []).filter((c) => !!c.recording_url);
    if (!search.trim()) return rec;
    const q = search.trim().toLowerCase();
    return rec.filter((c) => {
      const number = c.direction === "inbound" ? c.from_number : c.to_number;
      return (
        (c.customer_name || "").toLowerCase().includes(q) ||
        (number || "").toLowerCase().includes(q) ||
        (c.created_at && format(parseISO(c.created_at), "MMMM d, yyyy").toLowerCase().includes(q))
      );
    });
  }, [calls, search]);

  // Group by date label
  const grouped = useMemo(() => {
    const map = {};
    for (const c of recordings) {
      if (!c.created_at) continue;
      const d = parseISO(c.created_at);
      let label;
      if (isToday(d)) label = "Today";
      else if (isYesterday(d)) label = "Yesterday";
      else label = format(d, "EEEE, MMMM d");
      (map[label] ||= []).push(c);
    }
    // Preserve newest-first order using the order recordings already arrived in
    const orderedKeys = [];
    for (const c of recordings) {
      if (!c.created_at) continue;
      const d = parseISO(c.created_at);
      const k = isToday(d) ? "Today" : isYesterday(d) ? "Yesterday" : format(d, "EEEE, MMMM d");
      if (!orderedKeys.includes(k)) orderedKeys.push(k);
    }
    return orderedKeys.map((k) => ({ label: k, calls: map[k] }));
  }, [recordings]);

  // Only the active row mounts a player — switching rows (or toggling off)
  // unmounts the previous player, which stops its audio.
  const togglePlay = (call) => {
    const id = call.call_id || call.twilio_sid;
    setPlayingId((cur) => (cur === id ? null : id));
  };

  const buildFileName = (call) => {
    const callerNumber = call.direction === "inbound" ? call.from_number : call.to_number;
    const dateStr = call.created_at ? format(parseISO(call.created_at), "yyyy-MM-dd") : "recording";
    return `Call_Recording_${(call.customer_name || callerNumber || "call").replace(/[^a-z0-9]+/gi, "_")}_${dateStr}.mp3`;
  };

  const handleDownload = async (call) => {
    const id = call.call_id || call.twilio_sid;
    const url = getRecordingProxyUrl(call);
    if (!url) { toast.error("No recording URL available"); return; }
    setDownloadingId(id);
    const fileName = buildFileName(call);
    const token = localStorage.getItem("token");
    try {
      await downloadOrShareFile(url, fileName, "audio/mpeg", token);
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error("Download failed");
        window.open(url, "_blank");
      }
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="recordings-tab">
      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, number, or date…"
          className="pl-9"
          data-testid="recordings-search-input"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : recordings.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="recordings-empty">
          <Mic className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{search ? "No recordings match your search." : "No recordings yet. Recorded calls will appear here."}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.label}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
                {group.label}
              </h3>
              <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
                {group.calls.map((call) => {
                  const id = call.call_id || call.twilio_sid;
                  const isPlaying = playingId === id;
                  const number = call.direction === "inbound" ? call.from_number : call.to_number;
                  const audioUrl = getRecordingProxyUrl(call);
                  const dirIcon = call.direction === "inbound"
                    ? <PhoneIncoming className="w-3.5 h-3.5 text-green-500" />
                    : <PhoneOutgoing className="w-3.5 h-3.5 text-blue-500" />;
                  return (
                    <div key={id} className="flex items-center gap-3 p-3 hover:bg-muted/40 transition-colors" data-testid={`recording-row-${id}`}>
                      {/* Play/Pause */}
                      <Button
                        size="icon"
                        onClick={() => togglePlay(call)}
                        className={`h-10 w-10 rounded-full shrink-0 ${isPlaying ? "bg-blue-500 hover:bg-blue-600" : "bg-muted hover:bg-muted/80 text-foreground"}`}
                        data-testid={`recording-play-btn-${id}`}
                        aria-label={isPlaying ? "Pause" : "Play"}
                      >
                        {isPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 ml-0.5" />}
                      </Button>

                      {/* Shared player (mounted only for the active row) */}
                      {isPlaying && audioUrl && (
                        <div className="flex-1 min-w-0">
                          <CallRecordingPlayer
                            compact
                            autoPlay
                            src={audioUrl}
                            knownDuration={call.recording_duration || call.duration || 0}
                            fileName={buildFileName(call)}
                            showDownload={false}
                            onEnded={() => setPlayingId(null)}
                            testId={`recording-player-${id}`}
                          />
                        </div>
                      )}

                      {/* Caller info — collapses when playing to make room for the audio control */}
                      {!isPlaying && (
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {dirIcon}
                            <span className="text-foreground font-medium truncate">
                              {call.customer_name || formatPhoneNumber(number) || "Unknown"}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                            {call.customer_name && <span className="font-mono">{formatPhoneNumber(number)}</span>}
                            {call.customer_name && <span className="text-muted-foreground/40">·</span>}
                            <span>{call.created_at ? format(parseISO(call.created_at), "h:mm a") : "—"}</span>
                            {call.duration > 0 && (
                              <>
                                <span className="text-muted-foreground/40">·</span>
                                <span>{formatDuration(call.duration)}</span>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Download */}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDownload(call)}
                        disabled={downloadingId === id}
                        className="h-9 w-9 text-muted-foreground hover:text-foreground shrink-0"
                        data-testid={`recording-download-btn-${id}`}
                        aria-label="Download recording"
                      >
                        {downloadingId === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecordingsTab;
